import {
  SET_STEP_ONE_DATA,
  SET_STEP_TWO_DATA,
  RESET_USER_UPDATE_DATA,
  LOAD_USER_DATA,
  LOAD_USER_DATA_FROM_STORAGE,
  SET_IS_FORM_CHANGED,
  SET_STEPS_CHANGED,
  SAVE_FORM_DATA_TO_STORAGE,
  UPDATE_STORE_DATA,
} from './UserUpdate.constant';
import { IUserUpdateState, UserUpdateActionsTypes } from './UserUpdate.type';
import {
  saveFileToIndexedDB,
  clearAllUserUpdateFilesFromIndexedDB,
} from 'utils/indexedDB';

const getUserUpdateStorageKey = (userId?: number) =>
  `userUpdateData_${userId || 'unknown'}`;

const saveToLocalStorage = async (state: IUserUpdateState) => {
  try {
    if (!state.userId) return;

    const storageKey = getUserUpdateStorageKey(state.userId);

    const filePromises = state.stepTwo.details.map(async (detail, index) => {
      if (detail.file) {
        const fileId = `object-${state.userId}-${index}`;
        await saveFileToIndexedDB(fileId, detail.file);
        return fileId;
      }
      return null;
    });

    const fileIds = await Promise.all(filePromises);

    const serializableState = {
      ...state,
      stepTwo: {
        ...state.stepTwo,
        details: state.stepTwo.details.map((detail, index) => ({
          ...detail,
          fileId: detail.fileId || fileIds[index] || undefined,
          originalFilename: detail.originalFilename,
          file: undefined,
          id: detail.id,
          zones: detail.zones.map((zone) => ({
            ...zone,
            id: zone.id,
            photo: undefined,
          })),
          sensors: (detail.sensors || []).map((sensor) => ({
            ...sensor,
            id: sensor.id,
          })),
        })),
      },
    };

    localStorage.setItem(storageKey, JSON.stringify(serializableState));
  } catch (error) {
    console.warn('Failed to save user update data to localStorage:', error);
  }
};

const loadFromLocalStorage = (
  userId?: number,
): Partial<IUserUpdateState> | null => {
  try {
    if (!userId) return null;

    const storageKey = getUserUpdateStorageKey(userId);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      const parsed = JSON.parse(stored);

      const hasInvalidData = parsed.stepTwo?.details?.some(
        (detail: any) => detail.originalFilename && !detail.fileId,
      );

      if (hasInvalidData) {
        localStorage.removeItem(storageKey);
        return null;
      }

      return {
        ...parsed,
        stepTwo: {
          ...parsed.stepTwo,
          details: parsed.stepTwo.details.map((detail: any) => ({
            ...detail,
            file: undefined,
            zones: (detail.zones || []).map((zone: any) => ({
              ...zone,
              photo: undefined,
            })),
          })),
        },
      };
    }
  } catch (error) {
    console.warn('Failed to load user update data from localStorage:', error);
  }
  return null;
};

const initialZones = [
  {
    name: '',
    plantType: '',
    plantName: '',
    soilType: '',
    sensors: [],
  },
];

const initialDetails = [
  {
    name: '',
    file: undefined,
    zones: initialZones,
    sensors: [],
  },
];

const initialState: IUserUpdateState = {
  stepOne: {
    fullname: '',
    email: '',
    password: '',
    phoneCode: 'ae',
    phoneNumber: '',
    whatsappNotifications: false,
  },
  stepTwo: {
    details: initialDetails,
  },
  isFormChanged: false,
  isStepOneChanged: false,
  isStepTwoChanged: false,
  isLoading: false,
  isDataLoadedFromStorage: false,
};

export default function userUpdateReducer(
  state = initialState,
  action: UserUpdateActionsTypes,
): IUserUpdateState {
  switch (action.type) {
    case SET_STEP_ONE_DATA:
      return {
        ...state,
        stepOne: action.payload,
      };
    case SET_STEP_TWO_DATA:
      return {
        ...state,
        stepTwo: action.payload,
      };

    case LOAD_USER_DATA:
      if (!action.payload.skipLocalStorageSave) {
        saveToLocalStorage({
          ...state,
          userId: action.payload.userId,
          stepOne: action.payload.stepOne,
          stepTwo: action.payload.stepTwo,
          isFormChanged: false,
          isStepOneChanged: false,
          isStepTwoChanged: false,
        });
      }

      return {
        ...state,
        userId: action.payload.userId,
        stepOne: action.payload.stepOne,
        stepTwo: action.payload.stepTwo,
        isLoading: false,
        isFormChanged: false,
        isStepOneChanged: false,
        isStepTwoChanged: false,
        isDataLoadedFromStorage: action.payload.skipLocalStorageSave || false,
      };

    case LOAD_USER_DATA_FROM_STORAGE:
      const storedData = loadFromLocalStorage(action.payload.userId);
      if (storedData) {
        const hasDetails =
          storedData.stepTwo?.details && storedData.stepTwo.details.length > 0;
        const newState = {
          ...state,
          ...storedData,
          stepTwo: hasDetails
            ? storedData.stepTwo!
            : { details: initialDetails },
          userId: action.payload.userId,
          isLoading: false,
          isDataLoadedFromStorage: true,
          isFormChanged: false,
          isStepOneChanged: false,
          isStepTwoChanged: false,
        };
        return newState;
      } else {
        return { ...state, userId: action.payload.userId };
      }

    case RESET_USER_UPDATE_DATA:
      if (state.userId) {
        localStorage.removeItem(getUserUpdateStorageKey(state.userId));
      }
      clearAllUserUpdateFilesFromIndexedDB(state.userId).catch((error) =>
        console.warn('Failed to clear files from IndexedDB:', error),
      );
      return { ...initialState };

    case SET_IS_FORM_CHANGED:
      saveToLocalStorage({
        ...state,
        isFormChanged: action.payload,
      });

      return { ...state, isFormChanged: action.payload };

    case SET_STEPS_CHANGED: {
      const { isStepOneChanged, isStepTwoChanged } = action.payload;
      const isFormChanged = isStepOneChanged || isStepTwoChanged;

      saveToLocalStorage({
        ...state,
        isFormChanged,
        isStepOneChanged,
        isStepTwoChanged,
      });

      return {
        ...state,
        isFormChanged,
        isStepOneChanged,
        isStepTwoChanged,
      };
    }

    case SAVE_FORM_DATA_TO_STORAGE:
      const updatedState = {
        ...state,
        stepTwo: action.payload,
      };
      saveToLocalStorage(updatedState);
      return updatedState;

    case UPDATE_STORE_DATA:
      return {
        ...state,
        userId: action.payload.userId,
        stepOne: action.payload.stepOne,
        stepTwo: action.payload.stepTwo,
        isLoading: false,
      };

    default:
      return state;
  }
}
